interface PersonSearchResult {
  id: number;
  name: string;
  birthday?: string | null;
}

export default (element: HTMLElement) => {
  // Find search input in parent form (it's in a sibling form__control)
  const form = element.closest('.form');
  const searchInput = form?.querySelector<HTMLInputElement>(
    '[data-component-role="search-input"]',
  );
  const resultsContainer = element.querySelector<HTMLDivElement>(
    '[data-component-role="results"]',
  );
  const hiddenInput = element.querySelector<HTMLInputElement>(
    '[data-component-role="hidden-input"]',
  );

  if (!searchInput || !resultsContainer || !hiddenInput) {
    console.error('Missing component roles in pickPerson component');
    return;
  }

  const fieldName = hiddenInput.name || 'person_id';

  let debounceTimeout: number | null = null;
  let currentQuery = '';
  let abortController: AbortController | null = null;

  // Format birthday as DD.MM.YYYY
  const formatBirthday = (birthday: string | null | undefined): string => {
    if (!birthday) return '';
    const date = new Date(birthday);
    const day = date.getDate().toString();
    const month = (date.getMonth() + 1).toString();
    const year = date.getFullYear();
    return `${day}.${month}.${year}`;
  };

  // Render results
  const renderResults = (persons: PersonSearchResult[], isLoading: boolean) => {
    if (isLoading) {
      resultsContainer.innerHTML = `
        <div class="peoplePicker__noResultsMessage">
          Vyhledávání...
        </div>
      `;
      return;
    }

    if (persons.length === 0) {
      resultsContainer.innerHTML = `
        <div class="peoplePicker__noResultsMessage">
          Nebyli nalezeni žádní členové odpovídající zadaným kritériím.
        </div>
      `;
      return;
    }

    const listHtml = persons
      .map(
        (person) => `
        <label class="peoplePicker__item" data-person-item>
          <input type="radio" name="${fieldName}_radio" value="${person.id}" class="peoplePicker__input">
          <div class="peoplePicker__content">
            <div class="peoplePicker__name">${person.name}</div>
            <div class="peoplePicker__description">${formatBirthday(person.birthday)}</div>
          </div>
        </label>
      `,
      )
      .join('');

    resultsContainer.innerHTML = `<div class="peoplePicker__list">${listHtml}</div>`;

    // Add event listeners to radio buttons
    const radioButtons = resultsContainer.querySelectorAll<HTMLInputElement>(
      'input[type="radio"]',
    );
    radioButtons.forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          hiddenInput.value = radio.value;
          // Trigger change event for form validation
          hiddenInput.dispatchEvent(new Event('change', { bubbles: true }));
          hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      });
    });
  };

  // Show empty state
  const showEmptyState = () => {
    resultsContainer.innerHTML = `
      <div class="peoplePicker__noResultsMessage">
        Začni psát pro vyhledávání
      </div>
    `;
  };

  // Initialize with empty state
  showEmptyState();

  // Search function
  const performSearch = async (query: string) => {
    if (query.trim().length === 0) {
      showEmptyState();
      return;
    }

    // Abort any previous request
    if (abortController) {
      abortController.abort();
    }

    // Create new abort controller for this request
    abortController = new AbortController();
    const signal = abortController.signal;

    currentQuery = query;
    renderResults([], true);

    try {
      const response = await fetch(
        `/api/search-person?query=${encodeURIComponent(query)}`,
        { signal },
      );
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const persons: PersonSearchResult[] = await response.json();

      // Only update if this is still the current query
      if (query === currentQuery) {
        renderResults(persons, false);
      }
    } catch (error) {
      // Ignore abort errors - they're expected when a new search starts
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      console.error('Error searching persons:', error);
      if (query === currentQuery) {
        resultsContainer.innerHTML = `
          <div class="peoplePicker__noResultsMessage">
            Chyba při hledání. Zkuste to prosím znovu.
          </div>
        `;
      }
    }
  };

  // Debounced search on input
  searchInput.addEventListener('input', () => {
    const query = searchInput.value;

    if (debounceTimeout !== null) {
      window.clearTimeout(debounceTimeout);
    }

    debounceTimeout = window.setTimeout(() => {
      performSearch(query);
      debounceTimeout = null;
    }, 400);
  });

  // Reset hidden input on form reset
  hiddenInput.form?.addEventListener('reset', () => {
    hiddenInput.value = '';
    performSearch('');
  });
};
